import type { AdminAuditItem, AdminFeatureFlag, AdminFeedback, AdminOverview, AdminPlan, AdminUser, AdminWorkspace, ApiFailure, ChatbotAnalyticsPayload, ChatbotComment, ChatbotConversation, ChatbotFlow, ChatbotFlowsPayload, ConversationsPayload, FlowSimulationEvent, LinkedInAccountsPayload, MetaAccountsPayload, PlannerMedia, PlannerMediaPayload, PlannerPostsPayload, SessionPayload, SupportTicket, SupportTicketsPayload, TikTokAccountsPayload, WorkspaceSummary, YouTubeAccountsPayload } from './contracts';

export class ApiError extends Error {
  status: number;
  payload: ApiFailure;

  constructor(status: number, payload: ApiFailure) {
    super(payload.message || payload.error || 'Request failed');
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  let payload: unknown = {};
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(response.ok ? 502 : response.status, {
      error: 'api_unavailable',
      message: 'The application API is not available on this deployment yet.',
    });
  }
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  if (!response.ok) throw new ApiError(response.status, payload as ApiFailure);
  return payload as T;
}

export async function coreRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const response = await fetch(`/api/planner/core${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  return parseResponse<T>(response);
}

export async function appRequest<T>(path: string, workspaceId?: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (workspaceId) headers.set('X-Nexa-Workspace', workspaceId);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  return parseResponse<T>(response);
}

export const authApi = {
  me: () => coreRequest<SessionPayload>('/me'),
  login: (email: string, password: string) =>
    coreRequest<{ ok?: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (input: { name: string; workspace_name: string; email: string; password: string }) =>
    coreRequest<{ ok?: boolean }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  logout: () => coreRequest<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
};

export const workspaceApi = {
  create: (name: string) => coreRequest<{ workspace: WorkspaceSummary }>('/workspaces', { method: 'POST', body: JSON.stringify({ name }) }),
  rename: (workspaceId: string, name: string) => coreRequest<{ workspace: WorkspaceSummary }>(`/workspaces/${encodeURIComponent(workspaceId)}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  remove: (workspaceId: string) => coreRequest<{ ok: boolean; next_workspace_id?: string }>(`/workspaces/${encodeURIComponent(workspaceId)}`, { method: 'DELETE' }),
};

export const accountsApi = {
  list: (workspaceId: string) => appRequest<MetaAccountsPayload>('/api/meta/accounts', workspaceId),
  connectUrl: (workspaceId: string, channel: 'facebook' | 'instagram', mode: 'manage' | 'add' = 'manage', returnView: 'accounts' | 'planner' = 'accounts') => {
    const returnUrl = new URL(`${window.location.origin}/`);
    returnUrl.searchParams.set('view', returnView);
    returnUrl.searchParams.set('workspace', workspaceId);
    const params = new URLSearchParams({ workspace_id: workspaceId, return_to: returnUrl.toString() });
    if (channel === 'instagram') return `/auth/meta/instagram/start?${params.toString()}`;
    params.set('channel', 'facebook');
    params.set('mode', mode);
    return `/auth/meta/start?${params.toString()}`;
  },
};

export const youtubeApi = {
  list: (workspaceId: string) => appRequest<YouTubeAccountsPayload>('/api/youtube/accounts', workspaceId),
  connectUrl: (workspaceId: string, returnView: 'accounts' | 'planner' = 'accounts') => {
    const returnUrl = new URL(`${window.location.origin}/`);
    returnUrl.searchParams.set('view', returnView);
    returnUrl.searchParams.set('workspace', workspaceId);
    const params = new URLSearchParams({ workspace_id: workspaceId, return_to: returnUrl.toString() });
    return `/auth/youtube/start?${params.toString()}`;
  },
  disconnect: (workspaceId: string, connectionId: string) => appRequest<{ ok?: boolean }>(`/api/youtube/disconnect/${encodeURIComponent(connectionId)}`, workspaceId, { method: 'POST' }),
  refresh: (workspaceId: string, connectionId: string) => appRequest<{ ok?: boolean }>(`/api/youtube/refresh/${encodeURIComponent(connectionId)}`, workspaceId, { method: 'POST' }),
};

export const tiktokApi = {
  list: (workspaceId: string) => appRequest<TikTokAccountsPayload>('/api/tiktok/accounts', workspaceId),
  connectUrl: (workspaceId: string, returnView: 'accounts' | 'planner' = 'accounts') => {
    const returnUrl = new URL(`${window.location.origin}/`);
    returnUrl.searchParams.set('view', returnView);
    returnUrl.searchParams.set('workspace', workspaceId);
    const params = new URLSearchParams({ workspace_id: workspaceId, return_to: returnUrl.toString() });
    return `/auth/tiktok/start?${params.toString()}`;
  },
};

export const linkedinApi = {
  list: (workspaceId: string) => appRequest<LinkedInAccountsPayload>('/api/linkedin/accounts', workspaceId),
  connectUrl: (workspaceId: string, returnView: 'accounts' | 'planner' = 'accounts') => {
    const returnUrl = new URL(`${window.location.origin}/`);
    returnUrl.searchParams.set('view', returnView);
    returnUrl.searchParams.set('workspace', workspaceId);
    const params = new URLSearchParams({ workspace_id: workspaceId, return_to: returnUrl.toString() });
    return `/auth/linkedin/start?${params.toString()}`;
  },
  disconnect: (workspaceId: string, connectionId: string) => appRequest<{ ok?: boolean }>(`/api/linkedin/disconnect/${encodeURIComponent(connectionId)}`, workspaceId, { method: 'POST' }),
};

export const plannerApi = {
  sync: (workspaceId: string) => appRequest<{ ok?: boolean }>('/api/planner/sync', workspaceId, { method: 'POST' }),
  posts: (workspaceId: string) => appRequest<PlannerPostsPayload>('/api/planner/posts', workspaceId),
  media: (workspaceId: string) => appRequest<PlannerMediaPayload>('/api/planner/media', workspaceId),
  upload: async (workspaceId: string, file: File, kind: 'media' | 'thumbnail') => {
    const response = await fetch('/api/planner/media', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Nexa-Workspace': workspaceId,
        'x-file-name': encodeURIComponent(file.name),
        'x-file-type': file.type || 'application/octet-stream',
        'x-file-kind': kind,
      },
      body: file,
    });
    return parseResponse<{ media: PlannerMedia }>(response);
  },
  create: (workspaceId: string, payload: Record<string, unknown>) => appRequest<{ posts?: unknown[]; post?: unknown }>('/api/planner/posts', workspaceId, { method: 'POST', body: JSON.stringify(payload) }),
  updateGroup: (workspaceId: string, groupId: string, payload: Record<string, unknown>) => appRequest<{ posts?: unknown[] }>('/api/planner/groups/' + encodeURIComponent(groupId), workspaceId, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteGroup: (workspaceId: string, groupId: string) => appRequest<{ ok?: boolean }>('/api/planner/groups/' + encodeURIComponent(groupId), workspaceId, { method: 'DELETE' }),
};

const chatbotPath = '/api/planner/chatbot/flows';
export const chatbotApi = {
  flows: (workspaceId: string) => appRequest<ChatbotFlowsPayload>(chatbotPath, workspaceId),
  flow: (workspaceId: string, id: string) => appRequest<{ flow: ChatbotFlow }>(`${chatbotPath}/${encodeURIComponent(id)}`, workspaceId),
  createFlow: (workspaceId: string, payload: Partial<ChatbotFlow>) => appRequest<{ flow: ChatbotFlow }>(chatbotPath, workspaceId, { method: 'POST', body: JSON.stringify(payload) }),
  updateFlow: (workspaceId: string, id: string, payload: Partial<ChatbotFlow>) => appRequest<{ flow: ChatbotFlow }>(`${chatbotPath}/${encodeURIComponent(id)}`, workspaceId, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteFlow: (workspaceId: string, id: string) => appRequest<{ ok?: boolean }>(`${chatbotPath}/${encodeURIComponent(id)}`, workspaceId, { method: 'DELETE' }),
  validateFlow: (workspaceId: string, id: string) => appRequest<{ validation: { valid?: boolean; errors?: string[]; warnings?: string[] } }>(`${chatbotPath}/${encodeURIComponent(id)}/validate`, workspaceId),
  simulateFlow: (workspaceId: string, id: string, input: { message: string; inputs: string[]; mock_ai: boolean }) => appRequest<{ events?: FlowSimulationEvent[]; validation?: { valid?: boolean; errors?: string[]; warnings?: string[] } }>(`${chatbotPath}/${encodeURIComponent(id)}/simulate`, workspaceId, { method: 'POST', body: JSON.stringify(input) }),
  conversations: (workspaceId: string, query = '') => appRequest<ConversationsPayload>(`/api/planner/chatbot/conversations${query ? `?${query}` : ''}`, workspaceId),
  conversation: (workspaceId: string, id: string) => appRequest<{ conversation: ChatbotConversation }>(`/api/planner/chatbot/conversations/${encodeURIComponent(id)}`, workspaceId),
  reply: (workspaceId: string, id: string, text: string) => appRequest<{ ok?: boolean }>(`/api/planner/chatbot/conversations/${encodeURIComponent(id)}/messages`, workspaceId, { method: 'POST', body: JSON.stringify({ text, author: 'workspace_user' }) }),
  patchConversation: (workspaceId: string, id: string, kind: 'state' | 'assign' | 'tags', payload: Record<string, unknown>) => appRequest<{ conversation?: ChatbotConversation }>(`/api/planner/chatbot/conversations/${encodeURIComponent(id)}/${kind}`, workspaceId, { method: 'PATCH', body: JSON.stringify(payload) }),
  comments: (workspaceId: string, query = '') => appRequest<{ comments?: ChatbotComment[] }>(`/api/planner/chatbot/comments${query ? `?${query}` : ''}`, workspaceId),
  analytics: (workspaceId: string) => appRequest<ChatbotAnalyticsPayload>('/api/planner/chatbot/analytics', workspaceId),
};

export const supportApi = {
  tickets: (workspaceId: string) => coreRequest<SupportTicketsPayload>(`/workspaces/${encodeURIComponent(workspaceId)}/support/tickets`),
  createTicket: (workspaceId: string, input: { subject: string; category: string; description: string }) => coreRequest<{ ticket: SupportTicket }>(`/workspaces/${encodeURIComponent(workspaceId)}/support/tickets`, { method: 'POST', body: JSON.stringify(input) }),
  feedback: (workspaceId: string, input: { kind: string; rating: number; message: string; page_context: string }) => coreRequest<{ ok?: boolean }>(`/workspaces/${encodeURIComponent(workspaceId)}/feedback`, { method: 'POST', body: JSON.stringify(input) }),
};

export const adminApi = {
  overview: () => coreRequest<AdminOverview>('/admin/overview'),
  users: () => coreRequest<{ users?: AdminUser[] }>('/admin/users'),
  createUser: (input: Record<string, unknown>) => coreRequest<{ user?: AdminUser; workspace_id?: string; temp_password?: string }>('/admin/users', { method: 'POST', body: JSON.stringify(input) }),
  updateUser: (id: string, input: { status: string }) => coreRequest<{ user?: AdminUser }>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  workspaces: () => coreRequest<{ workspaces?: AdminWorkspace[] }>('/admin/workspaces'),
  updateWorkspace: (id: string, input: Record<string, unknown>) => coreRequest<{ workspace?: AdminWorkspace }>(`/admin/workspaces/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  plans: () => coreRequest<{ plans?: AdminPlan[] }>('/admin/plans'),
  createPlan: (input: Record<string, unknown>) => coreRequest<{ id: string }>('/admin/plans', { method: 'POST', body: JSON.stringify(input) }),
  updatePlan: (id: string, input: Record<string, unknown>) => coreRequest<{ plan?: AdminPlan }>(`/admin/plans/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  featureFlags: () => coreRequest<{ feature_flags?: AdminFeatureFlag[] }>('/admin/feature-flags'),
  updateFeatureFlag: (key: string, input: Record<string, unknown>) => coreRequest<{ feature_flag?: AdminFeatureFlag }>(`/admin/feature-flags/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  updateSubscription: (workspaceId: string, input: Record<string, unknown>) => coreRequest(`/admin/workspaces/${encodeURIComponent(workspaceId)}/subscription`, { method: 'PATCH', body: JSON.stringify(input) }),
  setOverride: (workspaceId: string, key: string, input: Record<string, unknown>) => coreRequest(`/admin/workspaces/${encodeURIComponent(workspaceId)}/overrides/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  audit: () => coreRequest<{ audit?: AdminAuditItem[] }>('/admin/audit'),
  tickets: () => coreRequest<{ tickets?: Array<SupportTicket & { workspace_name?: string; requester_email?: string; priority?: string }> }>('/admin/support/tickets'),
  updateTicket: (id: string, input: Record<string, unknown>) => coreRequest(`/admin/support/tickets/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  feedback: () => coreRequest<{ feedback?: AdminFeedback[] }>('/admin/support/feedback'),
  billingSweep: () => coreRequest<{ ok?: boolean }>('/admin/billing-sweep', { method: 'POST' }),
};
